import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { canManageUsers, UserRole } from '@cls/shared';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { SearchableSelect } from '../components/primitives/SearchableSelect.js';
import { AddUserModal, type UserInitial } from '../components/domain/AddUserModal.js';
import { InviteUsersModal } from '../components/domain/InviteUsersModal.js';

interface UserRecord {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
  scopedCustomerIds: string[];
  scopedProjectIds: string[];
  scopedAgencyCode: string | null;
  workerId: string | null;
  contactId: string | null;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface ProjectRef { id: string; code: string; name: string }
interface CustomerRef { id: string; name: string }
interface ContactRef { id: string; name: string; email: string | null }
interface WorkerRef { id: string; name: string; role: string | null }
interface AgencyRef { id: string; code: string; name: string }

const ROLE_LABELS: Record<UserRole, string> = {
  Admin: 'Admin',
  Manager: 'Manager',
  Supervisor: 'Supervisor',
  Worker: 'Worker',
};

const ROLE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  ...Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label })),
];

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

const AVC = ['#E8A427', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#10B981', '#EC4899', '#14B8A6'];
function avc(name: string): string {
  return AVC[name.charCodeAt(0) % AVC.length] ?? AVC[0]!;
}

function avEl(name: string, size = 38, font = 13): JSX.Element {
  return (
    <div className="av" style={{ width: size, height: size, fontSize: font, background: avc(name) }}>
      {initials(name)}
    </div>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

export function Users() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const isDirector = !!currentUser && canManageUsers(currentUser);

  const [search, setSearch] = useState('');
  const [fRole, setFRole] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<UserInitial | null>(null);

  useResetSub(() => { setSearch(''); setFRole(''); setFStatus(''); });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserRecord[]>('/users').then((r) => r.data),
    enabled: isDirector,
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectRef[]>('/projects').then((r) => r.data),
    enabled: isDirector,
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get<CustomerRef[]>('/customers').then((r) => r.data),
    enabled: isDirector,
  });
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get<ContactRef[]>('/contacts').then((r) => r.data),
    enabled: isDirector,
  });
  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerRef[]>('/workers').then((r) => r.data),
    enabled: isDirector,
  });
  const { data: agencies = [] } = useQuery({
    queryKey: ['agencies'],
    queryFn: () => api.get<AgencyRef[]>('/agencies').then((r) => r.data),
    enabled: isDirector,
  });

  const projectsById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const customersById = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
  const contactsById = useMemo(() => Object.fromEntries(contacts.map((c) => [c.id, c])), [contacts]);
  const workersById = useMemo(() => Object.fromEntries(workers.map((w) => [w.id, w])), [workers]);
  const agenciesByCode = useMemo(() => Object.fromEntries(agencies.map((a) => [a.code, a])), [agencies]);

  const filtered = useMemo(() => {
    let r = [...users];
    const s = search.toLowerCase().trim();
    if (s) r = r.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(s));
    if (fRole) r = r.filter((u) => u.role === fRole);
    if (fStatus === 'active') r = r.filter((u) => u.active);
    if (fStatus === 'inactive') r = r.filter((u) => !u.active);
    return r;
  }, [users, search, fRole, fStatus]);

  const active = activeId ? users.find((u) => u.id === activeId) ?? null : null;
  const isSelf = !!active && !!currentUser && active.id === currentUser.id;

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted');
      setActiveId(null);
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error('Delete failed', e?.response?.data?.message),
  });

  const resetPw = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => api.patch(`/users/${id}`, { password }),
    onSuccess: () => toast.success('Password reset'),
    onError: () => toast.error('Reset failed'),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) => api.patch(`/users/${id}`, { active: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: () => toast.error('Update failed'),
  });

  const resendInvite = useMutation({
    mutationFn: (id: string) => api.post<{ emailSent: boolean; inviteLink: string }>(`/users/${id}/resend-invite`).then((r) => r.data),
    onSuccess: async (data) => {
      await navigator.clipboard.writeText(data.inviteLink).catch(() => undefined);
      toast.success(
        data.emailSent ? 'Invite resent' : 'Invite link ready',
        data.emailSent ? 'Email sent · link also copied to clipboard' : 'Email could not be sent — link copied to clipboard, send it by WhatsApp',
      );
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error('Resend failed', e?.response?.data?.message),
  });

  const handleResetPw = (u: UserRecord) => {
    const pwd = window.prompt(`New password for ${u.name} (min 8 chars)`);
    if (pwd == null) return;
    if (pwd.length < 8) {
      toast.error('Password too short', 'Must be at least 8 characters');
      return;
    }
    resetPw.mutate({ id: u.id, password: pwd });
  };

  const handleDelete = (u: UserRecord) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})? This cannot be undone.`)) return;
    remove.mutate(u.id);
  };

  const clearFilters = () => { setSearch(''); setFRole(''); setFStatus(''); };

  if (!isDirector) {
    return (
      <div className="page" id="page-users">
        <div className="hbar">
          <div>
            <div className="ph">Users</div>
            <div className="ps">Manage user accounts and roles</div>
          </div>
        </div>
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
          🔒 Access Restricted — Director role required
        </div>
      </div>
    );
  }

  const activeCount = users.filter((u) => u.active).length;
  const sub = `${users.length} user${users.length !== 1 ? 's' : ''} · ${activeCount} active`;
  const countLabel = `${filtered.length} shown`;

  return (
    <div className="page" id="page-users">
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 110px)' }}>
          <div className="hbar">
            <div>
              <div className="ph">Users</div>
              <div className="ps" id="users-sub">{sub}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setInviteOpen(true)}>✉ Invite Users</button>
              <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ New User</button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 10, padding: '5px 8px', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)', minHeight: 36 }}>
            <div className="tbl-search" style={{ minWidth: 180, flex: 1, margin: 0 }}>
              <span style={{ color: 'var(--faint)' }}>⌕</span>
              <input
                id="users-search"
                placeholder="Search name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="snav-sep"></div>
            <div className="fpill">
              <span>Role:</span>
              <SearchableSelect
                id="users-f-role"
                value={fRole}
                onChange={setFRole}
                placeholder="All"
                options={ROLE_FILTER_OPTIONS}
              />
            </div>
            <div className="fpill">
              <span>Status:</span>
              <select id="users-f-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="snav-sep"></div>
            <span id="users-count" style={{ fontSize: 10, color: 'var(--muted)' }}>{countLabel}</span>
            <button className="btn btn-ghost btn-xs" onClick={clearFilters} title="Clear filters" style={{ marginLeft: 'auto' }}>✕ Clear</button>
          </div>

          <div className="g2" id="users-grid">
            {filtered.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--muted)', fontSize: 12, padding: 24 }}>
                No users match your filters.
              </div>
            ) : filtered.map((u) => (
              <div
                key={u.id}
                className="card"
                style={{ cursor: 'pointer', transition: 'border-color .15s', borderColor: activeId === u.id ? 'var(--accent)' : undefined }}
                onClick={() => setActiveId(u.id)}
              >
                <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 9 }}>
                  {avEl(u.name, 38, 13)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13 }}>{u.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <span className="pill">{ROLE_LABELS[u.role] ?? u.role}</span>
                  <span className={`badge ${u.active ? 'b-valid' : 'b-expired'}`}>{u.active ? 'Active' : 'Inactive'}</span>
                  {u.active && !u.lastLoginAt ? <span className="badge b-expiring">✉ Invited</span> : null}
                  {u.mfaEnabled ? <span className="badge b-tag">MFA</span> : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          id="users-dp"
          style={{ width: 480, minWidth: 480, background: 'var(--surface)', borderLeft: '1px solid var(--border)', overflowY: 'auto', padding: 16, flexShrink: 0, display: active ? 'block' : 'none', maxHeight: 'calc(100vh - 90px)' }}
        >
          {active ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 13 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {avEl(active.name, 42, 14)}
                  <div>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14 }}>{active.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{ROLE_LABELS[active.role] ?? active.role}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(active)}>✏ Edit</button>
                  <button className="close-btn" onClick={() => setActiveId(null)}>×</button>
                </div>
              </div>

              <div className="dp-row">
                <span className="dp-key">Email</span>
                <span className="dp-val">
                  <a href={`mailto:${active.email}`} style={{ color: 'var(--info)', fontSize: 12, textDecoration: 'none' }}>{active.email}</a>
                </span>
              </div>
              <div className="dp-row">
                <span className="dp-key">Status</span>
                <span className="dp-val">
                  <span className={`badge ${active.active ? 'b-valid' : 'b-expired'}`}>{active.active ? 'Active' : 'Inactive'}</span>
                </span>
              </div>
              <div className="dp-row">
                <span className="dp-key">MFA</span>
                <span className="dp-val">{active.mfaEnabled ? '✓ Enabled' : '— Disabled'}</span>
              </div>
              <div className="dp-row">
                <span className="dp-key">Last login</span>
                <span className="dp-val">{formatDateTime(active.lastLoginAt)}</span>
              </div>
              <div className="dp-row">
                <span className="dp-key">Created</span>
                <span className="dp-val">{formatDate(active.createdAt)}</span>
              </div>

              {(active.role === UserRole.Worker || active.role === UserRole.Supervisor) && active.workerId ? (
                <div className="dp-row">
                  <span className="dp-key">Worker</span>
                  <span
                    className="dp-val"
                    style={{ cursor: 'pointer', color: 'var(--info)' }}
                    onClick={() => navigate(`/workers?id=${active.workerId}`)}
                  >
                    {workersById[active.workerId]?.name ?? active.workerId}
                  </span>
                </div>
              ) : null}

              <div className="divider"></div>
              <div className="sec-title">Actions</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {active.active && !active.lastLoginAt ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => resendInvite.mutate(active.id)}
                    disabled={resendInvite.isPending}
                    title="Send a fresh set-password link and copy it to the clipboard"
                  >✉ Resend Invite</button>
                ) : null}
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleResetPw(active)}
                  disabled={resetPw.isPending}
                >🔑 Reset Password</button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => toggleActive.mutate({ id: active.id, next: !active.active })}
                  disabled={isSelf || toggleActive.isPending}
                >{active.active ? '⊘ Deactivate' : '✓ Activate'}</button>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--danger)' }}
                  onClick={() => handleDelete(active)}
                  disabled={isSelf || remove.isPending}
                >🗑 Delete</button>
              </div>
              {isSelf ? (
                <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 6 }}>
                  You can't deactivate or delete your own account.
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {addOpen ? <AddUserModal onClose={() => setAddOpen(false)} /> : null}
      {inviteOpen ? <InviteUsersModal onClose={() => setInviteOpen(false)} /> : null}
      {editing ? (
        <AddUserModal
          onClose={() => setEditing(null)}
          initial={editing}
          selfEdit={!!currentUser && editing.id === currentUser.id}
        />
      ) : null}
    </div>
  );
}
